import { useState, useEffect } from 'react';
import NewInput from 'app/components/NewInput';
import Dropdown from 'app/components/Dropdown';
import NewAttachment from 'app/components/NewAttachment';
import PageLoader from 'app/components/PageLoader';
import {
  getCustomerInfo,
  updateCustomerInfo,
  uploadGovtIdDocument,
} from 'app/api/customer';
import Message from 'app/utils/Message';
import Toast from 'app/utils/Toast';
import { commonMinDate, dataformatChangesYearToDate, replaceNAWithEmptyString } from 'app/utils/Parsers';

const CustomerInfo = (id) => {
  const [pageLoading, setPageLoading] = useState(false);
  const [data, setData] = useState();

  useEffect(() => {
    fetchCustomerInfo();
  }, []);

  // fetch customer information
  const fetchCustomerInfo = async () => {
    setPageLoading(true);
    const response = await getCustomerInfo(id?.id);
    setPageLoading(false);
    if (response?.responseCode === 200) {
      setData(response?.data);
    } else {
      Toast.error(
        response?.response?.message
          ? response?.response?.message
          : Message.Customer.STATUS_UPDATED_FAILURE
      );
    }
  };

  // update customer information
  const submitCustomerInfo = async () => {
    setPageLoading(true);

    const response = await updateCustomerInfo(id?.id, replaceNAWithEmptyString(data));
    setPageLoading(false);

    if (response?.responseCode === 200) {
      Toast.success(response?.message);
    } else {
      Toast.error(
        response?.response?.message
          ? response?.response?.message
          : Message.Customer.STATUS_UPDATED_FAILURE
      );
    }
  };

  // Uploads immediately on pick and keeps the returned S3 key in form state.
  // govtIdDocumentUrl is display-only (presigned, expires) and is never the
  // value we persist.
  const govtIdUploadHandler = async (file) => {
    if (!file) {
      setData((prevState) => ({
        ...prevState,
        govtIdDocument: null,
        govtIdDocumentUrl: null,
      }));
      return;
    }

    setPageLoading(true);
    const response = await uploadGovtIdDocument(file);
    setPageLoading(false);

    if (response?.responseCode === 200) {
      setData((prevState) => ({
        ...prevState,
        govtIdDocument: response?.data?.key,
      }));
      Toast.success(response?.message);
    } else {
      Toast.error(
        response?.response?.message
          ? response?.response?.message
          : Message.Error.COMMON_MESSAGE
      );
    }
  };

  // The detail endpoint returns the literal string "NA" for empty dates.
  // Passing that to moment yields "Invalid date", which <input type='date'>
  // rejects — hence the console warnings. Normalise to an empty value instead.
  // Fixed here rather than in dataformatChangesYearToDate, which has 37 call
  // sites across the app.
  const toDateInputValue = (value) => {
    if (!value || value === 'NA') return '';
    const formatted = dataformatChangesYearToDate(value);
    return formatted === 'Invalid date' ? '' : formatted;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const form = event.target;
    // Government ID is optional — no upload gate; the column is nullable
    // server-side.
    if (form.checkValidity()) {
      submitCustomerInfo();
    } else {
      form.reportValidity();
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <div className='inner-form-wrapper flex justify-between flex-wrap mb-4'>
          <div className='wd-33'>
            <NewInput
              label='Name*'
              id='name'
              type='text'
              placeholder='Name'
              name='name'
              value={data?.name || ''}
              onChange={(e) =>
                setData((prevState) => ({
                  ...prevState,
                  name: e.target.value,
                }))
              }
              required
            />
          </div>
          <div className='wd-33'>
            <NewInput
              label='Phone Number'
              id='number'
              type='text'
              placeholder='Phone Number'
              name='number'
              value={data?.phone || ''}
              disabled
            />
          </div>
          <div className='wd-33'>
            <NewInput
              label='Email Address*'
              id='email'
              type='text'
              pattern='[^@\s]+@[^@\s]+\.[^@\s]+'
              title='Invalid Email'
              placeholder='Email Address'
              name='email'
              value={data?.email || ''}
              required
              onChange={(e) =>
                setData((prevState) => ({
                  ...prevState,
                  email: e.target.value,
                }))
              }
            />
          </div>

          <div className='wd-50'>
            <NewInput
              label='Address'
              id='Address'
              type='text'
              placeholder='Address'
              name='Address'
              value={data?.address || ''}
              onChange={(e) =>
                setData((prevState) => ({
                  ...prevState,
                  address: e.target.value,
                }))
              }
            />
          </div>

          <div className='wd-50'>
            <label>Government ID</label>
            <NewAttachment
              id='govtIdDocument'
              label='Add Government ID'
              documentAllowed
              sizevalidationUpto={10}
              value={data?.govtIdDocumentUrl || null}
              onFileSelected={govtIdUploadHandler}
              alt='Government ID document'
            />
          </div>

          <div className='wd-50'>
            <Dropdown
              id={'gender'}
              label='Gender'
              name='gender'
              options={[
                {
                  label: 'Male',
                  value: 'Male',
                },
                {
                  label: 'Female',
                  value: 'Female',
                },
              ]}
              placeholder='Select Gender '
              value={data?.gender ? data?.gender : ''}
              onChange={(e) =>
                setData((prevState) => ({
                  ...prevState,
                  gender: e.value,
                }))
              }
            />
          </div>

          <div className='wd-50'>
            <NewInput
              label='DOB'
              id='DOB'
              placeholder='DOB'
              name='DOB'
              format='DD-MM-YYYY'
              type={'date'}
              // India today is both the earliest future date and the latest
              // past date, so commonMinDate() is used here as a maximum too.
              maxdate={commonMinDate()}
              value={toDateInputValue(data?.dob)}
              onChange={(e) =>
                setData((prevState) => ({
                  ...prevState,
                  dob: e.target.value,
                }))
              }
            />
          </div>

          <div className='wd-50'>
            <NewInput
              label='Anniversary Date'
              id='Anniversary'
              format='DD-MM-YYYY'
              type={'date'}
              // India today is both the earliest future date and the latest
              // past date, so commonMinDate() is used here as a maximum too.
              maxdate={commonMinDate()}
              placeholder='Anniversary Date'
              name='Anniversary'
              value={toDateInputValue(data?.anniversary)}
              onChange={(e) =>
                setData((prevState) => ({
                  ...prevState,
                  anniversary: e.target.value,
                }))
              }
            />
          </div>
        </div>

        <div className='d-flex justify-content-start'>
          <button className='button mt1' variant='Save'>
            Update
          </button>
        </div>
      </form>

      {pageLoading && <PageLoader pageLoading={pageLoading} />}
    </>
  );
};

export default CustomerInfo;
